import { Check } from 'lucide-react';

interface EditorStatusBarProps {
  line: number;
  column: number;
}

/** Mac reports "MacIntel" on Apple Silicon too; `maxTouchPoints` separates
 *  iPadOS, which reports the same platform but has no Cmd key to show. */
function isApplePlatform(): boolean {
  if (typeof navigator === 'undefined') return false;
  return /Mac|iPhone|iPad/.test(navigator.platform) && navigator.maxTouchPoints <= 1;
}

export function EditorStatusBar({ line, column }: EditorStatusBarProps) {
  const mod = isApplePlatform() ? '⌘' : 'Ctrl';

  return (
    <div className="flex items-center justify-between gap-3 border-t border-border bg-muted/30 px-3 py-1 text-xs text-muted-foreground">
      <span className="flex shrink-0 items-center gap-1 text-green-600 dark:text-green-500">
        <Check className="h-3 w-3" />
        Saved
      </span>
      {/*
        Run and Submit have had keyboard shortcuts since they were built and
        nothing on the page said so. The status bar is the one piece of chrome
        with spare room and a student's eye already on it.

        Hidden below sm: there is no hardware keyboard to use them with, and
        the bar is too narrow to spend on them.
      */}
      <span className="ml-auto hidden items-center gap-3 sm:flex">
        <span>
          Run <Key>{mod}</Key>
          <Key>↵</Key>
        </span>
        <span>
          Submit <Key>{mod}</Key>
          <Key>⇧</Key>
          <Key>↵</Key>
        </span>
      </span>
      <span className="shrink-0 tabular-nums">
        Ln {line}, Col {column}
      </span>
    </div>
  );
}

function Key({ children }: { children: React.ReactNode }) {
  return (
    <kbd className="ml-0.5 rounded border border-border border-b-2 bg-background px-1 py-px font-mono text-[10px] leading-none">
      {children}
    </kbd>
  );
}
